/**
 * AdvanceClaimModal (CC-Claims) — the FSM transition surface.
 *
 * Wraps the shared ReasonRequiredModal (the one "logged business reason" flow)
 * so the mandatory decision reason lands in the audit trail. The next-status
 * options come STRAIGHT from the salvaged FSM data (`CLAIM_STATUS_TRANSITIONS`):
 * the picker only ever offers legal moves, and the handler independently
 * returns 409 for any illegal transition — the acceptance's "illegal transition
 * blocked" holds on both the UI and the mock. A remedy is required (and shown)
 * only when approving or resolving.
 */

import { useEffect, useState } from 'react';
import { Input, Select, Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useFormStyles } from '@/styles/formStyles';
import {
  CLAIM_REMEDIES,
  CLAIM_STATUS_TRANSITIONS,
} from '@/types/api/claim';
import type { Claim, ClaimRemedy, ClaimStatus, TransitionClaimRequest } from '@/types/api/claim';
import { CLAIM_STATUS_TAG_COLOR } from './claimConstants';

const { Text } = Typography;

export interface AdvanceClaimModalProps {
  open: boolean;
  claim: Claim | null;
  onClose: () => void;
  /** Perform the transition; resolve `true` on success (keeps modal open on false). */
  onTransition: (payload: TransitionClaimRequest) => Promise<boolean>;
}

const remedyIsRequired = (status?: ClaimStatus) => status === 'approved' || status === 'resolved';

const AdvanceClaimModal = ({ open, claim, onClose, onTransition }: AdvanceClaimModalProps) => {
  const { t } = useTranslation(['claims', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();

  const allowed: readonly ClaimStatus[] = claim ? CLAIM_STATUS_TRANSITIONS[claim.status] : [];

  const [nextStatus, setNextStatus] = useState<ClaimStatus | undefined>();
  const [remedy, setRemedy] = useState<ClaimRemedy | undefined>();
  const [actor, setActor] = useState('');
  const [remedyError, setRemedyError] = useState(false);
  const [actorError, setActorError] = useState(false);

  useEffect(() => {
    if (open && claim) {
      const first = CLAIM_STATUS_TRANSITIONS[claim.status][0];
      setNextStatus(first);
      setRemedy(claim.remedy);
      setActor('');
      setRemedyError(false);
      setActorError(false);
    }
  }, [open, claim]);

  const needsRemedy = remedyIsRequired(nextStatus);

  const handleSubmit = async (reason: string): Promise<boolean> => {
    let valid = true;
    if (!actor.trim()) {
      setActorError(true);
      valid = false;
    }
    if (needsRemedy && !remedy) {
      setRemedyError(true);
      valid = false;
    }
    if (!nextStatus || !valid) return false;

    return onTransition({
      status: nextStatus,
      remedy: needsRemedy ? remedy : undefined,
      decisionReason: reason,
      actor: actor.trim(),
    });
  };

  return (
    <ReasonRequiredModal
      open={open}
      title={t('claims:transition.title')}
      description={
        claim
          ? t('claims:transition.description', { claimNumber: claim.claimNumber })
          : t('claims:transition.title')
      }
      submitText={t('claims:actions.advance')}
      cancelText={t('common:actions.cancel')}
      reasonLabel={t('claims:transition.reasonLabel')}
      requiredMessage={t('claims:transition.reasonRequired')}
      reasonPlaceholder={t('claims:transition.reasonPlaceholder')}
      onSubmit={handleSubmit}
      onCancel={onClose}
    >
      {claim && (
        <div style={{ marginBottom: token.marginMD }}>
          {/* Current status */}
          <div style={{ marginBottom: token.marginSM }}>
            <Text style={formStyles.labelStyle}>{t('claims:transition.currentStatus')}</Text>
            <div>
              <Tag color={CLAIM_STATUS_TAG_COLOR[claim.status]} style={{ margin: 0 }}>
                {t(`claims:statuses.${claim.status}`)}
              </Tag>
            </div>
          </div>

          {/* Next status — a picker only when the FSM offers a choice */}
          <div style={{ marginBottom: token.marginSM }}>
            <Text style={formStyles.labelStyle}>{t('claims:transition.toStatus')}</Text>
            {allowed.length > 1 ? (
              <Select<ClaimStatus>
                value={nextStatus}
                onChange={(value) => {
                  setNextStatus(value);
                  setRemedyError(false);
                }}
                size="large"
                style={{ ...formStyles.inputStyle, width: '100%' }}
                options={allowed.map((status) => ({
                  value: status,
                  label: t(`claims:statuses.${status}`),
                }))}
              />
            ) : (
              <div>
                <Tag
                  color={nextStatus ? CLAIM_STATUS_TAG_COLOR[nextStatus] : 'default'}
                  style={{ margin: 0 }}
                >
                  {nextStatus ? t(`claims:statuses.${nextStatus}`) : '-'}
                </Tag>
              </div>
            )}
          </div>

          {/* Remedy — required only when approving or resolving */}
          {needsRemedy && (
            <div style={{ marginBottom: token.marginSM }}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: 4 }}>*</span>
                {t('claims:transition.remedyLabel')}
              </Text>
              <Select<ClaimRemedy>
                value={remedy}
                onChange={(value) => {
                  setRemedy(value);
                  setRemedyError(false);
                }}
                size="large"
                style={{ ...formStyles.inputStyle, width: '100%' }}
                status={remedyError ? 'error' : undefined}
                options={CLAIM_REMEDIES.map((value) => ({
                  value,
                  label: t(`claims:remedies.${value}`),
                }))}
              />
              {remedyError && (
                <Text style={formStyles.errorStyle}>{t('claims:transition.remedyRequired')}</Text>
              )}
            </div>
          )}

          {/* Handled by */}
          <div>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: 4 }}>*</span>
              {t('claims:transition.actorLabel')}
            </Text>
            {/* Local controlled input — the reason field below owns RHF state. */}
            <Input
              value={actor}
              onChange={(e) => {
                setActor(e.target.value);
                if (e.target.value.trim()) setActorError(false);
              }}
              size="large"
              status={actorError ? 'error' : undefined}
              style={formStyles.inputStyle}
            />
            {actorError && (
              <Text style={formStyles.errorStyle}>{t('claims:transition.actorRequired')}</Text>
            )}
          </div>
        </div>
      )}
    </ReasonRequiredModal>
  );
};

export default AdvanceClaimModal;
